// ---------------------------------------------------------------------------
// Action Constants — Single Source of Truth for action type category sets
// and runtime reference names.
//
// All action type lists derive from action-registry.ts actionCategories.
// Other modules import from here instead of maintaining hardcoded copies.
// ---------------------------------------------------------------------------

import { actionCategories, actionRegistry } from '@/features/flow-editor/registry/action-registry'
import type { VisualCategory } from '@/features/flow-editor/models/flow-models'

// ---------------------------------------------------------------------------
// RUNTIME_REFS — variable names filtered out of expression analysis
// ---------------------------------------------------------------------------

/** Runtime-scoped variable names that should not appear in usage indexes. */
export const RUNTIME_REFS = new Set([
  'response', 'event', 'item', 'index', 'error',
  'true', 'false', 'null', 'undefined',
])

// ---------------------------------------------------------------------------
// Category helpers derived from actionCategories (action-registry.ts)
// ---------------------------------------------------------------------------

/** Map from category label to its action type list. */
const categoryLabelToActions = new Map<string, string[]>(
  actionCategories.map((cat) => [cat.label, cat.actions]),
)

/** Get action types for a given category label (e.g. 'Data Fetch', 'Navigation'). */
export function getActionsByCategoryLabel(label: string): string[] {
  return categoryLabelToActions.get(label) ?? []
}

// ---------------------------------------------------------------------------
// Reverse lookup: actionType → VisualCategory (from actionRegistry)
// ---------------------------------------------------------------------------

const actionTypeToCategoryMap = new Map<string, VisualCategory>()

for (const [actionType, config] of Object.entries(actionRegistry)) {
  actionTypeToCategoryMap.set(actionType, config.category)
}

/** Look up the VisualCategory for a given action type from the registry. */
export function getVisualCategoryFromRegistry(actionType: string): VisualCategory | undefined {
  return actionTypeToCategoryMap.get(actionType)
}
